import { ProductConfig, FeatureFlags, TeamLogoOptions, Participant } from '@draftkings/sportsbook-common-contracts';
import {
    buildTeamLogoOptions,
    getCountryFlagByCountryId,
    getLogoUrl,
    getTeamLogoUrlByTeamId
} from '@draftkings/sportsbook-common-utils';

export type GetTeamLogoUrlProps = {
    // TODO: Update Participant type to come from data layer once data layer is updated to version >=3.0.10 in LiG Widget
    participant?: Participant;
    logos: Record<string, string>;
    productConfig: ProductConfig;
    staticS3Host: string;
    featureFlags: FeatureFlags;
};

/**
 * Gets the URL for a team logo based on configuration and feature flags.
 *
 * - When the Swoosh flag is enabled and the participant is a team, returns a Swoosh logo.
 * - When the flag is enabled but the participant is NOT a team, returns undefined (no fallback).
 * - Otherwise, returns the legacy logo URL from logos.json.
 */
export const getTeamLogoUrl = ({
    participant,
    logos,
    productConfig,
    staticS3Host,
    featureFlags
}: GetTeamLogoUrlProps): string | undefined => {
    if (!participant) {
        return undefined;
    }

    const isSwooshTeamLogosEnabled = featureFlags['RLS-OSB-WEB-IsSwooshTeamLogosEnabled'].LivePage;

    if (isSwooshTeamLogosEnabled) {
        const isTeam = participant.type === 'Team';
        // @ts-expect-error - TODO: Remove this line once data layer is updated to version >=3.0.10 in LiG Widget
        const isNationalTeam = participant.isNationalTeam || false;

        if (!isTeam) {
            return undefined;
        }

        if (isNationalTeam) {
            const options: TeamLogoOptions = buildTeamLogoOptions('sm', 'dark');
            // @ts-expect-error - TODO: Remove this line once data layer is updated to version >=3.0.10 in LiG Widget
            return getCountryFlagByCountryId(productConfig.swooshBaseUrl, participant.countryCode, options)?.toString();
        }

        const teamId = participant.metadata?.rosettaTeamId || participant.id;
        const logoUrl = getTeamLogoUrlByTeamId(
            productConfig.swooshBaseUrl,
            teamId,
            buildTeamLogoOptions('sm', 'dark', participant.venueRole)
        );

        return logoUrl?.toString();
    }

    return getLogoUrl(staticS3Host, logos, participant.name);
};
